import {
  QueryClient,
  QueryClientContext,
  useQuery,
  type QueryClient as QueryClientType,
} from "@tanstack/react-query";
import { useContext, useEffect, useRef } from "react";

/** Long enough to cover a canvas switch and back; short enough that a
 *  session's worth of documents does not sit in memory for hours. */
export const FILE_RECORD_TEXT_GC_TIME_MS = 30 * 60_000;

/** Its own root, outside `["fileRecords"]`: a list invalidation must not
 *  re-read bodies that are already keyed by version. The app's persister
 *  denylists the root, and `meta.persist` says so too. */
export function fileRecordTextQueryKey(versionKey: string) {
  return ["fileRecordText", versionKey] as const;
}

let fallbackClient: QueryClientType | null = null;

/** The app's client when mounted under one; a private client otherwise, so
 *  a card rendered outside the app (stories, tests) still caches. */
function useTextQueryClient(): QueryClientType {
  const fromContext = useContext(QueryClientContext);
  if (fromContext) return fromContext;
  fallbackClient ??= new QueryClient();
  return fallbackClient;
}

/** Whether two version keys name versions of the same file. */
function samePath(a: unknown, b: string): boolean {
  return (
    typeof a === "string" &&
    a.slice(0, a.lastIndexOf("#")) === b.slice(0, b.lastIndexOf("#"))
  );
}

export interface UseFileRecordTextParams {
  /** Presigned URL to read from. Only the latest one is used; rotation
   *  alone never re-reads. */
  url: string | null | undefined;
  /** {@link fileRecordVersionKey} of the record — the cache key. */
  versionKey: string;
  enabled?: boolean;
}

/**
 * The text of a file version, shared by every card and viewer that shows
 * it: a rebuilt card paints from the cache, and only a new version (or a
 * read that failed) touches the network.
 */
export function useFileRecordText({
  url,
  versionKey,
  enabled = true,
}: UseFileRecordTextParams) {
  const client = useTextQueryClient();
  const urlRef = useRef(url);
  urlRef.current = url;
  const query = useQuery(
    {
      queryKey: fileRecordTextQueryKey(versionKey),
      queryFn: async () => {
        const target = urlRef.current;
        if (!target) throw new Error("File record content URL required");
        const res = await fetch(target);
        if (!res.ok) {
          throw new Error(
            `Failed to fetch file record content: HTTP ${res.status}`,
          );
        }
        return res.text();
      },
      enabled: enabled && !!url && versionKey !== "#",
      staleTime: Infinity,
      gcTime: FILE_RECORD_TEXT_GC_TIME_MS,
      meta: { persist: false },
      // A new version of the same file shows the old text until its own
      // arrives. Another file's body is never a placeholder for this one:
      // the viewers are not keyed per file, and the seed path would write
      // one file's text into another.
      placeholderData: (previous: string | undefined, previousQuery) =>
        samePath(previousQuery?.queryKey[1], versionKey) ? previous : undefined,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
    },
    client,
  );

  // A read that failed on a dead link is asked again when a fresh link
  // lands; a read that succeeded is not.
  const { isError, refetch } = query;
  useEffect(() => {
    if (isError && url) void refetch();
    // Only a link change may re-ask.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return query;
}
